// Import FingerprintJS Pro SDK
import FingerprintJSPro from '@fingerprintjs/fingerprintjs-pro'

/**
 * Resolves a stable visitor id for analytics / guest project association.
 *
 * Must never hard-fail Capacitor (or web) cold start: FingerprintJS Pro loads a remote script and
 * will reject offline. A missing API key soft-fails the same way.
 *
 * Only a real Pro id is written to the year-long cookie. A local fallback stays in memory for the
 * session, so an offline cold start does not pin the visitor to a synthetic id for a year.
 */
export default defineNuxtPlugin(async () => {
    // Get runtime config (environment variables defined in nuxt.config)
    const runtimeConfig = useRuntimeConfig() as {
        public?: {
            fingerprintApiKey?: string
            fingerprintCookie?: string
        }
    }

    const fingerprintKey = runtimeConfig.public?.fingerprintApiKey
    /** Cookie name from `NUXT_PUBLIC_FINGERPRINT_COOKIE` (Bitwarden per env). */
    const fpCookie = runtimeConfig.public?.fingerprintCookie ?? 'cr_visitorId'
    const fingerPrintCookie = useAppCookie<string>(fpCookie, { maxAge: 365 * 24 * 60 * 60 })

    let visitorId: string | null = fingerPrintCookie.get()

    if (!visitorId) {
        // Only a Pro id earns the cookie: caching a `local-` id would stop Pro from ever retrying.
        const proVisitorId = await resolveProVisitorId(fingerprintKey)
        if (proVisitorId) {
            fingerPrintCookie.setIfExists(proVisitorId)
        }
        visitorId = proVisitorId ?? createLocalVisitorId()
    }

    return {
        provide: {
            fingerprintId: visitorId,
        },
    }
})

/**
 * FingerprintJS Pro visitor id, or `null` when it cannot run (no API key, offline, blocked script,
 * FP outage). Callers decide the fallback; this never invents an id, so only real Pro ids are
 * cacheable.
 */
async function resolveProVisitorId(fingerprintKey: string | undefined): Promise<string | null> {
    if (!fingerprintKey) {
        return null
    }

    try {
        const fp = await FingerprintJSPro.load({
            apiKey: fingerprintKey,
            endpoint: ['https://a.crane.com', FingerprintJSPro.defaultEndpoint],
            scriptUrlPattern: [
                `https://a.crane.com/web/v<version>/<apiKey>/loader_v<loaderVersion>.js`,
                FingerprintJSPro.defaultScriptUrlPattern,
            ],
        })
        const result = await fp.get()
        if (result.visitorId) {
            return result.visitorId
        }
    } catch {
        // Offline / blocked / FP outage — boot with a local id instead of error.vue.
    }

    return null
}

/**
 * Session-only visitor id used when FingerprintJS Pro cannot run. Deliberately not cached: the
 * next launch should retry Pro rather than reuse this.
 */
function createLocalVisitorId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `local-${crypto.randomUUID()}`
    }
    return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
